/**
 * ThemePreviewBar — o único jeito de sair de um test-drive de tema.
 *
 * Mora global (App.tsx) de propósito: o preview vale o app inteiro, então a
 * saída precisa existir em qualquer rota. Sem ela o usuário fica vestindo um
 * tema que não é o dele, sem entender por quê e sem botão de voltar — que é
 * exatamente a armadilha que a feature poderia criar.
 *
 * Também é o ponto de conversão: quem gostou compra dali mesmo, sem voltar
 * pra loja e procurar o card de novo.
 *
 * Posicionamento: pílula flutuante `fixed`, ancorada acima da BottomNav
 * (`z-50` — acima da nav `z-40`, abaixo de modais `z-60`, conforme a
 * hierarquia global da skill `ux-mechanics`). O offset é fixo em vez de
 * condicional à nav porque nem toda rota tem BottomNav; nas que não têm, a
 * pílula só flutua um pouco mais alto, o que é indistinguível de intencional.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Eye, Paintbrush, X } from "lucide-react";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { getTheme } from "../../config/themeRegistry";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../contexts/ThemeContext";
import { useThemePreview } from "../../contexts/ThemePreviewContext";
import { useToast } from "../../contexts/ToastContext";
import { useUserStatsContext } from "../../contexts/UserStatsContext";
import { useUnlockedThemes } from "../../features/store/hooks/useUnlockedThemes";
import {
  spendCoins,
  TRANSACTION_REASONS,
  InsufficientFundsError,
} from "../../services/firebase/economyService";
import { updateUserProfile } from "../../services/firebase/userService";
import { themeEquipUpdate } from "../../lib/theme/themeEquip";
import { track } from "../../lib/telemetry";
import { trackProCtaClick, trackProNudgeView } from "../../features/pro/proTelemetry";
import { useTrialOffer } from "../../features/pro/hooks/useTrialOffer";
import { isPurchaseAvailable } from "../../features/pro/purchaseAvailability";

export function ThemePreviewBar() {
  const { previewThemeId, stopPreview } = useThemePreview();
  const { equippedTheme } = useTheme();
  const { user, effectiveIsPro } = useAuth();
  const { stats } = useUserStatsContext();
  const { unlockedIds } = useUnlockedThemes();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const trialDays = useTrialOffer();

  // Impressão do convite: a pessoa está VESTINDO um tema do PRO que não tem.
  // É o momento de maior intenção do app inteiro para o teste.
  const previewTier = previewThemeId ? getTheme(previewThemeId).tier : null;
  const previewIsProUpsell =
    !!user?.uid && !effectiveIsPro && isPurchaseAvailable() && (previewTier === "pro" || previewTier === "premium");
  useEffect(() => {
    if (previewIsProUpsell) trackProNudgeView("theme_preview_bar", trialDays);
  }, [previewIsProUpsell, trialDays]);

  // Fim automático quando o tema testado vira o tema real (comprou e equipou,
  // ou equipou por Settings/Meus Itens). Encerrar por efeito, e não no clique,
  // evita o flash do tema antigo entre `stopPreview()` e o perfil atualizar.
  useEffect(() => {
    if (previewThemeId && equippedTheme === previewThemeId) stopPreview();
  }, [previewThemeId, equippedTheme, stopPreview]);

  if (!previewThemeId || !user?.uid) return null;

  const theme = getTheme(previewThemeId);
  const isSubscriptionTheme = theme.tier === "pro" || theme.tier === "premium";
  const owned =
    theme.tier === "free" ||
    unlockedIds.includes(theme.id) ||
    (isSubscriptionTheme && effectiveIsPro);
  const price = theme.price?.coins ?? 0;

  const rawCoins = stats?.wallet?.coins;
  const coins =
    typeof rawCoins === "number" && Number.isFinite(rawCoins) ? Math.max(0, Math.floor(rawCoins)) : 0;
  const missing = Math.max(0, price - coins);

  function handleExit() {
    track("theme_preview_end", { themeId: previewThemeId, action: "dismiss" });
    stopPreview();
  }

  /** Não chama stopPreview: o efeito acima encerra quando o perfil chega. */
  async function equip(themeId: string) {
    if (!user?.uid) return;
    await updateUserProfile(user.uid, themeEquipUpdate(themeId, equippedTheme));
  }

  async function handleEquip() {
    if (busy) return;
    setBusy(true);
    try {
      await equip(theme.id);
      track("theme_preview_end", { themeId: theme.id, action: "equip" });
      showToast("Tema equipado!", { type: "success" });
    } catch {
      showToast("Não foi possível equipar o tema. Tente novamente.", { type: "error" });
    } finally {
      setBusy(false);
    }
  }

  async function handleBuy() {
    if (busy || !user?.uid) return;
    setBusy(true);
    try {
      // Mesma chave determinística da loja — comprar pelo preview ou pelo card
      // é a MESMA compra, e um retry nunca debita duas vezes.
      const key = `THEME_PURCHASE_${theme.id}_${user.uid}`;
      await spendCoins(user.uid, price, TRANSACTION_REASONS.THEME_PURCHASE, key, theme.id);
      // Equipa na sequência — o usuário já está vestindo o tema, e tirá-lo da
      // tela logo depois de pagar seria o oposto do que ele acabou de pedir.
      await equip(theme.id);
      track("theme_preview_purchase", { themeId: theme.id, price });
      showToast("Tema desbloqueado e equipado!", { type: "success" });
    } catch (e) {
      if (e instanceof InsufficientFundsError) {
        showToast("Saldo insuficiente para esta compra.", { type: "error" });
      } else {
        showToast("Ocorreu um erro. Tente novamente.", { type: "error" });
      }
    } finally {
      setBusy(false);
    }
  }

  function handleGoPro() {
    track("theme_preview_upsell_click", { themeId: theme.id });
    trackProCtaClick("theme_preview_bar");
    navigate("/pro");
  }

  // Button sem `size` não injeta padding/radius (escape hatch documentado no
  // DS) — a pílula precisa ser bem menor que o `sm`.
  const ctaClass = "shrink-0 gap-1 px-3 py-1.5 text-[11px] font-bold";

  const cta = (() => {
    if (owned) {
      return (
        <Button className={ctaClass} onClick={() => void handleEquip()} isLoading={busy}>
          {!busy && <Paintbrush className="h-3 w-3" aria-hidden />}
          {busy ? "Aplicando..." : "Equipar"}
        </Button>
      );
    }
    if (isSubscriptionTheme) {
      // Build nativo: "Assinar PRO" leva ao checkout, então vira estado em vez
      // de convite — a mesma forma do "Faltam N 🪙" logo abaixo, que este
      // arquivo já usa para "você não pode ter isto agora" sem oferecer nada.
      // Dizer que o tema é do PRO é informação e pode ficar; oferecer a
      // assinatura é o que a diretriz 3.1.1 proíbe. Ver
      // `features/pro/purchaseAvailability.ts`.
      if (!isPurchaseAvailable()) {
        return (
          <span className="shrink-0 rounded-full bg-surface-alt px-3 py-1.5 font-body text-[11px] font-semibold text-ink">
            Só PRO
          </span>
        );
      }
      return (
        <Button className={ctaClass} onClick={handleGoPro}>
          {trialDays ? `${trialDays} dias grátis` : "Assinar PRO"}
        </Button>
      );
    }
    if (missing > 0) {
      return (
        <span className="shrink-0 rounded-full bg-surface-alt px-3 py-1.5 font-body text-[11px] font-semibold text-soft">
          Faltam {missing} 🪙
        </span>
      );
    }
    return (
      <Button className={ctaClass} onClick={() => void handleBuy()} isLoading={busy}>
        {!busy && <Coins className="h-3 w-3" aria-hidden />}
        {busy ? "Processando..." : `Comprar · ${price}`}
      </Button>
    );
  })();

  return (
    <div
      className="pointer-events-none fixed left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 justify-center px-4"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 68px)" }}
      role="status"
    >
      <div className="pointer-events-auto flex max-w-full items-center gap-2 rounded-full border border-line bg-surface py-1.5 pl-3 pr-1.5 shadow-lg">
        <span className="flex min-w-0 items-center gap-1.5">
          <Eye className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
          <span className="truncate font-body text-[11px] text-soft">
            Testando <span className="font-bold text-ink">{theme.label}</span>
          </span>
        </span>

        {cta}

        <IconButton
          icon={X}
          label="Sair do preview e voltar ao meu tema"
          variant="ghost"
          size="sm"
          onClick={handleExit}
          className="shrink-0 rounded-full"
        />
      </div>
    </div>
  );
}
